/**
 * Welche Abbuchung landet in welchem Monat.
 *
 * Bewusst keine Umlage: ein Jahresvertrag erscheint einmal in voller Hoehe an
 * dem Monat, in dem er faellig wird - nicht als Zwoelftel ueber das Jahr
 * verteilt. Genau darum geht es bei der Frage "was kommt auf uns zu".
 *
 * Wo Angaben fehlen, wird geschaetzt und das auch so ausgewiesen. Eine Zahl,
 * die nur so tut, als waere sie sicher, ist schlimmer als eine mit Sternchen.
 */
import type { BillingCycle, ItemMeta } from './types.js';
import { periodMoney } from './money.js';

type Forecastable = Pick<
  ItemMeta,
  | 'id'
  | 'title'
  | 'hasCost'
  | 'status'
  | 'cycle'
  | 'priceCents'
  | 'currency'
  | 'priceIsPerSeat'
  | 'priceIsNet'
  | 'vatRatePercent'
  | 'seats'
  | 'billingDay'
  | 'renewalDate'
  | 'purchaseDate'
  | 'createdAt'
>;

export interface ForecastCharge {
  itemId: string;
  title: string;
  /** ISO-Datum der Abbuchung. */
  date: string;
  grossCents: number;
  cycle: BillingCycle;
  /** true, wenn das Datum geraten ist, weil Abbuchungstag oder Verlängerung fehlen. */
  estimated: boolean;
}

export interface ForecastMonth {
  /** '2026-09' */
  key: string;
  label: string;
  year: number;
  /** 0-11 */
  month: number;
  charges: ForecastCharge[];
  grossCents: number;
  hasEstimates: boolean;
}

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Der 31. in einem 30-Tage-Monat wird zum letzten Tag, nicht zum Ersten des naechsten. */
function clampDay(year: number, month: number, day: number): Date {
  return new Date(year, month, Math.min(day, daysInMonth(year, month)));
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Erzeugt die Abbuchungstermine eines Vertrags innerhalb des Fensters.
 * `from` ist der erste Tag des ersten Monats, `to` der letzte Tag des letzten.
 */
function chargesFor(item: Forecastable, from: Date, to: Date): ForecastCharge[] {
  if (item.hasCost === false || item.status === 'cancelled' || item.cycle === 'free') return [];

  const grossCents = periodMoney(item).grossCents;
  if (grossCents === 0) return [];

  const make = (date: Date, estimated: boolean): ForecastCharge => ({
    itemId: item.id,
    title: item.title,
    date: toIso(date),
    grossCents,
    cycle: item.cycle,
    estimated,
  });

  const inWindow = (d: Date): boolean => d >= from && d <= to;
  const out: ForecastCharge[] = [];

  if (item.cycle === 'onetime') {
    const kauf = parseDate(item.purchaseDate);
    if (kauf && inWindow(kauf)) out.push(make(kauf, false));
    return out;
  }

  if (item.cycle === 'monthly') {
    const geschaetzt = item.billingDay === null;
    const tag = item.billingDay ?? 1;
    const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
    while (cursor <= to) {
      const termin = clampDay(cursor.getFullYear(), cursor.getMonth(), tag);
      if (inWindow(termin)) out.push(make(termin, geschaetzt));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return out;
  }

  if (item.cycle === 'quarterly' || item.cycle === 'yearly') {
    const schritt = item.cycle === 'quarterly' ? 3 : 12;
    // Ohne Verlängerungsdatum bleibt nur der Jahrestag der Anlage - geraten,
    // aber besser als den Vertrag ganz zu verschweigen.
    const anker = parseDate(item.renewalDate) ?? parseDate(item.createdAt?.slice(0, 10) ?? null);
    const geschaetzt = item.renewalDate === null;
    if (!anker) return out;

    // Vom Anker aus vor- oder zurückgehen, bis das Fenster erreicht ist.
    const cursor = new Date(anker);
    while (cursor > from) cursor.setMonth(cursor.getMonth() - schritt);
    while (cursor < from) cursor.setMonth(cursor.getMonth() + schritt);
    while (cursor <= to) {
      out.push(make(new Date(cursor), geschaetzt));
      cursor.setMonth(cursor.getMonth() + schritt);
    }
    return out;
  }

  if (item.cycle === 'weekly') {
    // Ohne bekannten Wochentag wird ab heute gerechnet.
    const cursor = new Date(from);
    while (cursor <= to) {
      if (inWindow(cursor)) out.push(make(new Date(cursor), true));
      cursor.setDate(cursor.getDate() + 7);
    }
    return out;
  }

  return out;
}

/** Die kommenden `months` Monate, beginnend mit dem laufenden. */
export function buildForecast(
  items: Forecastable[],
  months = 12,
  from = new Date(),
): ForecastMonth[] {
  const start = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(from.getFullYear(), from.getMonth() + months, 0);

  const buckets = new Map<string, ForecastMonth>();
  for (let i = 0; i < months; i += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    buckets.set(monthKey(d), {
      key: monthKey(d),
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      year: d.getFullYear(),
      month: d.getMonth(),
      charges: [],
      grossCents: 0,
      hasEstimates: false,
    });
  }

  for (const item of items) {
    for (const charge of chargesFor(item, start, end)) {
      const bucket = buckets.get(charge.date.slice(0, 7));
      if (!bucket) continue;
      bucket.charges.push(charge);
      bucket.grossCents += charge.grossCents;
      if (charge.estimated) bucket.hasEstimates = true;
    }
  }

  for (const bucket of buckets.values()) {
    bucket.charges.sort((a, b) => a.date.localeCompare(b.date) || b.grossCents - a.grossCents);
  }

  return [...buckets.values()];
}

/** Verträge, bei denen ein Datum fehlt und deshalb geraten werden musste. */
export function itemsNeedingDates(items: Forecastable[]): Forecastable[] {
  return items.filter((i) => {
    if (i.hasCost === false || i.status === 'cancelled') return false;
    if (i.cycle === 'monthly') return i.billingDay === null;
    if (i.cycle === 'quarterly' || i.cycle === 'yearly') return i.renewalDate === null;
    if (i.cycle === 'weekly') return true;
    if (i.cycle === 'onetime') return i.purchaseDate === null;
    return false;
  });
}
